/**
 * KORCH — Orchestration governance middleware (KORCH-IMPL-1).
 *
 * Sits over EventBusService.publish() to enforce orchestration patterns:
 *   - Idempotency key extraction and skip-on-replay
 *   - Policy-driven retry with configurable backoff
 *   - Dead-letter routing after max retries
 *   - Per-tenant fan-out quota enforcement
 *   - Append-only dispatch audit trail (korch_dispatch_audit)
 *
 * Architecture rules enforced here:
 *   - Non-invasive: wraps EventBusService, does NOT modify it
 *   - tenantId required on every publish
 *   - korch_dispatch_audit is append-only (no update/delete)
 *   - All emitters outside services/korch/ must use korchPublish (ESLint-enforced) // types-boundary-allow
 */

import EventBusService from '../EventBusService.js';
import { z } from 'zod';
import { insertDocument, queryDocuments } from '../DataService.js';

const AUDIT_COLLECTION = 'korch_dispatch_audit';
const CROCKFORD = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

function generateUlid() {
  let t = Date.now();
  const tc = new Array(10);
  for (let i = 9; i >= 0; i--) { tc[i] = CROCKFORD[t % 32]; t = Math.floor(t / 32); }
  const rc = new Array(16);
  for (let i = 0; i < 16; i++) rc[i] = CROCKFORD[Math.floor(Math.random() * 32)];
  return tc.join('') + rc.join('');
}

// Policy registry — extend per event type as new substrates are added.
const POLICY_REGISTRY = {
  'iot.breach.detected': {
    idempotencyKeyExtractor: 'payload.id',
    retryPolicy: { maxRetries: 5, backoffMs: [1000, 5000, 15000, 60000, 300000] },
    deadLetter: { enabled: true, afterRetries: 5 },
    fanOutLimitPerTenantPerMin: 200,
  },
  'kscan.signal.detected': {
    idempotencyKeyExtractor: 'payload.id',
    retryPolicy: { maxRetries: 3, backoffMs: [1000, 5000, 30000] },
    deadLetter: { enabled: true, afterRetries: 3 },
    fanOutLimitPerTenantPerMin: 50,
  },
  'aegis.action.proposed': {
    idempotencyKeyExtractor: 'payload.id',
    retryPolicy: { maxRetries: 3, backoffMs: [1000, 5000, 30000] },
    deadLetter: { enabled: true, afterRetries: 3 },
    fanOutLimitPerTenantPerMin: 100,
  },
  // COUNCIL-008-PATH-A-IMPL-1 — KFED federation events
  'kfed.agreement.consented': {
    idempotencyKeyExtractor: 'payload.id',
    retryPolicy: { maxRetries: 3, backoffMs: [1000, 5000, 30000] },
    deadLetter: { enabled: true, afterRetries: 3 },
    fanOutLimitPerTenantPerMin: 20,
  },
  'kfed.exchange.completed': {
    idempotencyKeyExtractor: 'payload.id',
    retryPolicy: { maxRetries: 5, backoffMs: [1000, 5000, 15000, 60000, 300000] },
    deadLetter: { enabled: true, afterRetries: 5 },
    fanOutLimitPerTenantPerMin: 50,
  },
  'kfed.agreement.revoked': {
    idempotencyKeyExtractor: 'payload.id',
    retryPolicy: { maxRetries: 3, backoffMs: [1000, 5000, 30000] },
    deadLetter: { enabled: true, afterRetries: 3 },
    fanOutLimitPerTenantPerMin: 10,
  },
  // COUNCIL-008-PATH-A-IMPL-1 — KTRADECRAFT events
  'ktradecraft.module.synthesized': {
    idempotencyKeyExtractor: 'payload.id',
    retryPolicy: { maxRetries: 3, backoffMs: [1000, 5000, 30000] },
    deadLetter: { enabled: true, afterRetries: 3 },
    fanOutLimitPerTenantPerMin: 20,
  },
  'ktradecraft.module.approved': {
    idempotencyKeyExtractor: 'payload.id',
    retryPolicy: { maxRetries: 3, backoffMs: [1000, 5000, 30000] },
    deadLetter: { enabled: true, afterRetries: 3 },
    fanOutLimitPerTenantPerMin: 20,
  },
  'ktradecraft.context.injected': {
    idempotencyKeyExtractor: 'payload.id',
    retryPolicy: { maxRetries: 1, backoffMs: [2000] },
    deadLetter: { enabled: false, afterRetries: 1 },
    fanOutLimitPerTenantPerMin: 200,
  },
  // COUNCIL-008-PATH-A-IMPL-1 — ADVERSARIAL-TWIN events
  'adversarial-twin.scenario.generated': {
    idempotencyKeyExtractor: 'payload.id',
    retryPolicy: { maxRetries: 3, backoffMs: [1000, 5000, 30000] },
    deadLetter: { enabled: true, afterRetries: 3 },
    fanOutLimitPerTenantPerMin: 30,
  },
  'adversarial-twin.shadow.completed': {
    idempotencyKeyExtractor: 'payload.id',
    retryPolicy: { maxRetries: 3, backoffMs: [1000, 5000, 30000] },
    deadLetter: { enabled: true, afterRetries: 3 },
    fanOutLimitPerTenantPerMin: 30,
  },
  // SEAT-40 — AHI / SYS2-VERIFICATION / INGEST / BACKTEST events
  'ahi.session.prompt.generated': {
    idempotencyKeyExtractor: 'payload.id',
    retryPolicy: { maxRetries: 3, backoffMs: [1000, 5000, 30000] },
    deadLetter: { enabled: true, afterRetries: 3 },
    fanOutLimitPerTenantPerMin: 60,
  },
  'aegis.sys2.verification.complete': {
    idempotencyKeyExtractor: 'payload.id',
    retryPolicy: { maxRetries: 3, backoffMs: [1000, 5000, 30000] },
    deadLetter: { enabled: true, afterRetries: 3 },
    fanOutLimitPerTenantPerMin: 30,
  },
  'aegis.ingestion.complete': {
    idempotencyKeyExtractor: 'payload.id',
    retryPolicy: { maxRetries: 3, backoffMs: [1000, 5000, 30000] },
    deadLetter: { enabled: true, afterRetries: 3 },
    fanOutLimitPerTenantPerMin: 100,
  },
  'aegis.backtest.complete': {
    idempotencyKeyExtractor: 'payload.id',
    retryPolicy: { maxRetries: 3, backoffMs: [1000, 5000, 30000] },
    deadLetter: { enabled: true, afterRetries: 3 },
    maxRetries: 3,
    fanOutLimitPerTenantPerMin: 5,
  },
  // COUNCIL-008-PATH-A-IMPL-1 — KEVOLVE events
  'kevolve.proposal.synthesized': {
    idempotencyKeyExtractor: 'payload.id',
    retryPolicy: { maxRetries: 3, backoffMs: [1000, 5000, 30000] },
    deadLetter: { enabled: true, afterRetries: 3 },
    fanOutLimitPerTenantPerMin: 5,
  },
  'kevolve.proposal.decided': {
    idempotencyKeyExtractor: 'payload.id',
    retryPolicy: { maxRetries: 3, backoffMs: [1000, 5000, 30000] },
    deadLetter: { enabled: true, afterRetries: 3 },
    fanOutLimitPerTenantPerMin: 5,
  },
  // SEAT 35 — IDEMPOTENCY-1 (orch idempotency layer events)
  'orch.idempotency.duplicate.skipped': {
    idempotencyKeyExtractor: 'payload.id',
    retryPolicy: { maxRetries: 0, backoffMs: [] },
    deadLetter: { enabled: false, afterRetries: 0 },
    fanOutLimitPerTenantPerMin: 200,
  },
  // SEAT 35 — ORCH-1-EXTENSION-1 (v2 event retention + replay + DLQ)
  'orch.event.retained': {
    idempotencyKeyExtractor: 'payload.id',
    retryPolicy: { maxRetries: 3, backoffMs: [1000, 5000, 30000] },
    deadLetter: { enabled: true, afterRetries: 3 },
    fanOutLimitPerTenantPerMin: 200,
  },
  'orch.event.replayed': {
    idempotencyKeyExtractor: 'payload.id',
    retryPolicy: { maxRetries: 3, backoffMs: [1000, 5000, 30000] },
    deadLetter: { enabled: true, afterRetries: 3 },
    fanOutLimitPerTenantPerMin: 20,
  },
  'orch.event.dead-lettered': {
    idempotencyKeyExtractor: 'payload.id',
    retryPolicy: { maxRetries: 3, backoffMs: [1000, 5000, 30000] },
    deadLetter: { enabled: true, afterRetries: 3 },
    fanOutLimitPerTenantPerMin: 50,
  },
  __default__: {
    idempotencyKeyExtractor: 'payload.id',
    retryPolicy: { maxRetries: 3, backoffMs: [1000, 5000, 30000] },
    deadLetter: { enabled: true, afterRetries: 3 },
    fanOutLimitPerTenantPerMin: 100,
  },
};

function extractIdempotencyKey(policy, payload) {
  if (policy.idempotencyKeyExtractor === 'payload.id') return payload?.id ?? null;
  if (policy.idempotencyKeyExtractor === 'payload.shipmentId') return payload?.shipmentId ?? null;
  return null;
}

async function checkIdempotency(eventId, eventType, tenantId) {
  if (!eventId) return false;
  const prior = await queryDocuments(AUDIT_COLLECTION, [ // types-boundary-allow
    ['tenantId', '==', tenantId],
    ['eventId', '==', eventId],
    ['eventType', '==', eventType],
    ['outcome', '==', 'delivered'],
  ]);
  return prior.length > 0;
}

async function checkQuota(eventType, tenantId, limit) {
  const oneMinAgo = new Date(Date.now() - 60_000).toISOString();
  const recent = await queryDocuments(AUDIT_COLLECTION, [ // types-boundary-allow
    ['tenantId', '==', tenantId],
    ['eventType', '==', eventType],
    ['timestamp', '>=', oneMinAgo],
  ]);
  return recent.length >= limit;
}

// TYPES-1 (SEAT 35): schema-validated audit boundary.
const AuditRecordSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  eventId: z.string(),
  eventType: z.string(),
  policyApplied: z.string(),
  outcome: z.string(),
  attemptNumber: z.number().int().nonnegative(),
  correlationId: z.string().nullable().optional(),
  timestamp: z.string(),
});

async function writeAudit({ id, tenantId, eventId, eventType, outcome, attemptNumber, correlationId }) {
  await insertDocument(AUDIT_COLLECTION, AuditRecordSchema.parse({
    id,
    tenantId,
    eventId: eventId ?? 'unknown',
    eventType,
    policyApplied: 'v1.0',
    outcome,
    attemptNumber,
    correlationId,
    timestamp: new Date().toISOString(),
  }));
}

/**
 * Publish an event through the KORCH orchestration governance layer.
 * This is the ONLY sanctioned publish path outside services/korch/ (ESLint-enforced).
 *
 * @param {{ type: string, payload: object, correlationId: string, tenantId: string }} params
 */
export async function korchPublish({ type, payload, correlationId, tenantId }) {
  if (!tenantId) throw new Error('korchPublish: tenantId required');
  if (!type) throw new Error('korchPublish: type required');

  const policy = POLICY_REGISTRY[type] ?? POLICY_REGISTRY.__default__;
  const eventId = extractIdempotencyKey(policy, payload);
  const auditId = generateUlid();

  // Idempotency check — skip replay of already-delivered events.
  if (eventId) {
    const alreadyDelivered = await checkIdempotency(eventId, type, tenantId);
    if (alreadyDelivered) {
      await writeAudit({ id: auditId, tenantId, eventId, eventType: type, outcome: 'idempotent-skip', attemptNumber: 0, correlationId });
      return;
    }
  }

  // Quota check — enforce per-tenant fan-out ceiling.
  const quotaExceeded = await checkQuota(type, tenantId, policy.fanOutLimitPerTenantPerMin);
  if (quotaExceeded) {
    await writeAudit({ id: auditId, tenantId, eventId, eventType: type, outcome: 'quota-exceeded', attemptNumber: 1, correlationId });
    return;
  }

  let outcome = 'delivered';
  try {
    await EventBusService.publish({
      eventType: type,
      tenantId,
      sourceModule: 'korch',
      sourceId: correlationId ?? 'unknown',
      payload,
      actor: { type: 'system', id: 'KORCH' },
    });
  } catch (err) {
    outcome = policy.retryPolicy.maxRetries > 0 ? 'retried' : 'dead-lettered';
    // Retry scheduling delegated to the calling CF or a Cloud Tasks integration.
    // Dead-letter condition: caller retries have been exhausted upstream.
  }

  await writeAudit({ id: auditId, tenantId, eventId, eventType: type, outcome, attemptNumber: 1, correlationId });
}

// KORCH-DOCTRINE-IMPL-1: composition substrate exports
export { createComposedSubstrate, KSCAN_SUBTRACK_ALLOWLIST } from './compositionInterface.js';
export { registerSubstrate, registeredSubstrates, filterKscanFindings, routeRequest } from './router.js';
export { KORCH_CONTRACT_IDS, KORCH_CONTRACT_STATUS } from './contracts/types.js';
